import { useCallback, useEffect, useId, useMemo, useState } from 'react'

/** Hover/focus handlers to spread onto the element the tooltip is anchored to. */
export interface TooltipTriggerProps {
  onMouseEnter: () => void
  onMouseLeave: () => void
  onFocus: () => void
  onBlur: () => void
}

export interface TooltipTrigger {
  /** Whether the bubble should currently be rendered. Always false when disabled. */
  open: boolean
  /** Stable id for the bubble, for callers that want to wire `aria-describedby`. */
  tooltipId: string
  triggerProps: TooltipTriggerProps
  /** Force the bubble closed (e.g. the trigger's value just changed). */
  close: () => void
}

/**
 * Open/close state for a hover- *and* focus-triggered tooltip.
 *
 * Focus is handled alongside hover on purpose: a tooltip that only responds to
 * the mouse is invisible to keyboard users (WCAG 2.1 "Content on Hover or
 * Focus"). Escape also dismisses it, for the same reason.
 *
 * There is intentionally no open/close delay — the project runs `vi.useFakeTimers()`
 * globally in `src/test/setup.ts`, and a timer here would make every consumer's
 * tests timer-aware for no user-visible benefit on a text-reveal tooltip.
 */
export function useTooltipTrigger({ disabled = false }: { disabled?: boolean } = {}): TooltipTrigger {
  const tooltipId = useId()
  const [hovered, setHovered] = useState(false)

  const open = hovered && !disabled

  const close = useCallback(() => setHovered(false), [])

  // A tooltip that survives Escape traps the user: it can cover the control
  // they are trying to reach and there is no other way to dismiss it without
  // moving focus away.
  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setHovered(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open])

  // Drop the open state the moment the tooltip is disabled (e.g. the sidebar
  // widened and the text is no longer clipped) so no orphan bubble is left
  // pinned to an element that no longer has anything to reveal.
  useEffect(() => {
    if (disabled) setHovered(false)
  }, [disabled])

  const triggerProps = useMemo<TooltipTriggerProps>(
    () => ({
      onMouseEnter: () => setHovered(true),
      onMouseLeave: () => setHovered(false),
      onFocus: () => setHovered(true),
      onBlur: () => setHovered(false),
    }),
    []
  )

  return { open, tooltipId, triggerProps, close }
}
